import SitePhoto from '@/components/Photo'
import type { Photo } from '@/content/sections'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import { useState } from 'react'
import { Ctas, InlineText, type SectionOf } from './shared'

const GRID = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }

/**
 * One of the two carousel arrows: a 44px outlined circle, which is the smallest
 * comfortable tap target and the size the pills elsewhere already land on.
 *
 * Outlined rather than filled because a solid blue disc here would out-shout the
 * page's one real CTA; it fills on hover instead, which is the same "felt rather
 * than hinted" hover the buttons in `index.css` use. The chevron is inline SVG —
 * the site ships no icon set, and one glyph is not a reason to add one.
 */
function CarouselArrow({
  direction,
  label,
  onClick,
}: {
  direction: 'prev' | 'next'
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground transition duration-200 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-[0_6px_18px_hsl(var(--primary)/0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={direction === 'prev' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'} />
      </svg>
    </button>
  )
}

/**
 * The band split in two: the argument's headline stays put on the left while one
 * item at a time is shown on the right.
 *
 * Paragraphs in a grid are columns of small print with no order to read them in;
 * a panel each makes every item a single focus and lets the type grow to the
 * size the argument deserves. Holding the heading still beside it means the
 * reader never loses what the items are answering.
 *
 * **Clicked, not swiped.** This replaced a CSS scroll-snap track, which is the
 * cheaper mechanism and works without JavaScript — but a horizontal scroller on
 * a desktop trackpad is a guess, and the panel had to stop short of the band's
 * width so the next one peeked in, which is what made it look like a card in a
 * box. Buttons say what they do. That costs JavaScript, which is affordable
 * here and only here: written pages hydrate (`prerender.tsx` sets
 * `hydrate: true` for them), unlike the 117 imported documents. Do not carry
 * this shape onto one of those — it would render item one and never move.
 *
 * No frame and no counter, by request and in line with the rest of the site:
 * sections are separated by background colour, not by bordered cards. The note
 * sits above the title as an uppercase eyebrow — the same shape the solution
 * cards use — because blue sentence-case under a heading reads as a link.
 *
 * **Arrows lead, bars follow** (asked for 2026-08-26: *"alttaki yerlere basmak
 * hem aklımıza gelmiyor hem de nispeten zor"*). The 6px bars are a legend, not
 * a control — they say which one you are on, and nobody's first instinct is
 * to press one. So a prev/next pair sits at the head of that row at the site's
 * 44px tap size, and the bars stay beside them as the position read-out and a
 * way to jump. The arrows **wrap** rather than disable at the ends: a disabled
 * arrow on a short loop is a dead control half the time, and this band is
 * an argument you can enter anywhere.
 *
 * **Every pane is rendered, stacked in one grid cell**, and only opacity and a
 * 1rem lift change. That is what makes the swap a fade instead of a jump,
 * and it also removes the jolt the old `min-h` floor was patching: the cell is
 * always as tall as the longest pane, so nothing reflows. It also means every
 * paragraph is in the prerendered HTML rather than only the first, which is a
 * small gain for anyone reading without JavaScript.
 */
function FeatureCarousel({
  heading,
  headingImage,
  sub,
  items,
  photo,
}: {
  heading?: string
  headingImage?: Photo
  sub?: string
  items: { title: string; note?: string; body?: string }[]
  photo?: Photo
}) {
  const { t } = useI18n()
  const [active, setActive] = useState(0)
  const count = items.length
  if (!count) return null
  const current = Math.min(active, count - 1)
  const go = (delta: number) => setActive((i) => (Math.min(i, count - 1) + delta + count) % count)

  return (
    <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-20">
      <div>
        {/* Lettering instead of type, where the section carries the artwork. The
            words are the same words, so the `<h2>` stays and the drawing sits
            inside it with the heading as its `alt` — the band keeps one real
            heading in the document outline either way. `max-w-lg` is below the
            artwork's own 585px, because a wordmark enlarged past the size it
            was drawn at is the one way to make it look cheap. */}
        {headingImage && heading ? (
          <h2>
            <SitePhoto
              photo={headingImage}
              sizes="(min-width: 1024px) 40vw, 92vw"
              className="h-auto w-full max-w-lg"
            />
          </h2>
        ) : (
          heading && (
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{heading}</h2>
          )
        )}
        {sub && <p className="mt-6 text-lg text-muted-foreground">{sub}</p>}
        {/* The heading and the standfirst use maybe half this column's height
            and the rest was white, which read as the band having been cut off.
            16:9 because every source here already is, so the crop is nothing.
            `sizes` is the left half of the row, not the whole page — get that
            wrong and a phone downloads the 1280px file to paint 640. */}
        {photo && (
          <SitePhoto
            photo={photo}
            sizes="(min-width: 1024px) 44vw, 92vw"
            className="mt-10 aspect-[16/9] w-full rounded-3xl object-cover"
          />
        )}
      </div>

      <div>
        <div className="grid" aria-live="polite">
          {items.map((it, i) => (
            <div
              key={it.title}
              // Every pane occupies the same cell, so the stack is as tall as
              // the tallest and the fade happens in place.
              className={`col-start-1 row-start-1 transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none ${
                i === current
                  ? 'opacity-100 translate-y-0'
                  : 'pointer-events-none translate-y-4 opacity-0'
              }`}
              // A hidden pane is still in the layout, so it has to be taken out
              // of the accessibility tree by hand. `aria-hidden` is enough here
              // because these panes hold no links or buttons — nothing to tab to.
              aria-hidden={i !== current}
            >
              {it.note && (
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">
                  {it.note}
                </p>
              )}
              <h3 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">{it.title}</h3>
              {it.body && (
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
                  {it.body}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Arrows first, then the bars. The bar is 6px tall; the button around
            it is 24px, because a 6px target is a target you miss. */}
        <div className="mt-8 flex items-center gap-5">
          <div className="flex items-center gap-2">
            <CarouselArrow direction="prev" label={t.ui.previous} onClick={() => go(-1)} />
            <CarouselArrow direction="next" label={t.ui.next} onClick={() => go(1)} />
          </div>
          <div className="flex items-center gap-3">
            {items.map((it, i) => (
              <button
                key={it.title}
                type="button"
                onClick={() => setActive(i)}
                aria-label={it.title}
                aria-current={i === current}
                className="group py-3 focus-visible:outline-none"
              >
                <span
                  className={`block h-1.5 rounded-[--radius-pill] transition-all [transition-duration:var(--transition-base)] group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-4 ${
                    i === current
                      ? 'w-12 bg-primary'
                      : 'w-7 bg-border group-hover:bg-muted-foreground'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * digitopia.co's Our Values shape, measured from the live page rather than
 * invented: a stack of full-width light-grey cards, each with its title in brand
 * blue on the left and the words on the right.
 *
 * Why it is not the grid: these items are declarations, several short lines each,
 * and two columns of them turned every value into a wrapped paragraph — the line
 * breaks *are* the writing here ("We dream big. / We are confident."), and a grid
 * cell narrow enough to fit two across cannot keep them. One item per row gives
 * the right column enough measure that each line lands where it was written.
 *
 * The card is `bg-band` on a white page, which is why `background()` in
 * ContentPage keeps this variant out of the grey/white alternation: a grey card
 * on a grey band has no edge.
 *
 * `lines` is the intended field; an item with only a `body` still draws, as one
 * paragraph, so the shape degrades rather than breaks on an item that has not
 * been broken into lines. The type is `foreground`, not `muted` — on the
 * reference these are the page's statements, not supporting copy under a heading.
 */
function FeatureRows({ items }: { items: SectionOf<'features'>['items'] }) {
  return (
    <div className="space-y-8 sm:space-y-10">
      {items.map((it) => (
        <div
          key={it.title}
          className="grid gap-6 rounded-[40px] bg-band px-8 py-10 sm:px-14 sm:py-14 md:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] md:gap-12"
        >
          <h3 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
            {it.title}
          </h3>
          <div className="text-lg leading-relaxed">
            {it.lines
              ? it.lines.map((line) => <p key={line}>{line}</p>)
              : it.body && <p>{it.body}</p>}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function FeaturesSection({ section }: { section: SectionOf<'features'> }) {
  const { locale } = useI18n()
  const head = (
    <>
      {section.heading && (
        <h2 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
          {section.heading}
        </h2>
      )}
      {section.sub && (
        <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{section.sub}</p>
      )}
    </>
  )
  const foot = section.footnote ? (
    <p className="mt-10 max-w-3xl text-lg leading-relaxed">{section.footnote}</p>
  ) : null

  if (section.variant === 'rows') {
    return (
      <>
        {head}
        <div className={section.heading || section.sub ? 'mt-12' : ''}>
          <FeatureRows items={section.items} />
        </div>
        {foot}
        {section.cta && (
          <div className="mt-10">
            <Ctas items={[section.cta]} />
          </div>
        )}
      </>
    )
  }

  if (section.variant === 'carousel') {
    return (
      <>
        <FeatureCarousel
          heading={section.heading}
          headingImage={section.headingImage}
          sub={section.sub}
          items={section.items}
          photo={section.photo}
        />
        {foot}
      </>
    )
  }

  return (
    <>
      {head}
      <div className={`mt-12 grid gap-10 ${GRID[section.columns ?? 3]}`}>
        {section.items.map((it) => (
          <div key={it.title}>
            {/* A portrait, where the grid is people. Square and cropped, so
                three headshots shot on three different days still line up;
                `sizes` is one column of the grid, not the page. */}
            {it.photo &&
              (it.href ? (
                <a
                  href={localizeHref(it.href, locale)}
                  {...(it.href.startsWith('http')
                    ? { target: '_blank', rel: 'noreferrer noopener' }
                    : {})}
                  className="mb-6 block"
                >
                  <SitePhoto
                    photo={it.photo}
                    sizes="(min-width: 1024px) 22vw, (min-width: 640px) 35vw, 70vw"
                    className="mx-auto aspect-square w-full max-w-[220px] rounded-2xl object-cover"
                  />
                </a>
              ) : (
                <SitePhoto
                  photo={it.photo}
                  sizes="(min-width: 1024px) 22vw, (min-width: 640px) 35vw, 70vw"
                  className="mx-auto mb-6 aspect-square w-full max-w-[220px] rounded-2xl object-cover"
                />
              ))}
            <h3
              className={`font-bold tracking-tight ${it.photo ? 'text-center' : ''} ${
                section.textSize === 'lg' ? 'text-xl' : 'text-lg'
              }`}
            >
              {it.href ? (
                <a
                  href={localizeHref(it.href, locale)}
                  {...(it.href.startsWith('http')
                    ? { target: '_blank', rel: 'noreferrer noopener' }
                    : {})}
                >
                  {it.title}
                </a>
              ) : (
                it.title
              )}
            </h3>
            {it.note && (
              <p
                className={`mt-1 font-semibold uppercase tracking-[0.14em] text-primary ${
                  it.photo ? 'text-center' : ''
                } ${section.textSize === 'lg' ? 'text-sm' : 'text-xs'}`}
              >
                {it.note}
              </p>
            )}
            {it.body && (
              <p
                className={`mt-3 leading-relaxed text-muted-foreground ${
                  it.photo ? 'text-center' : ''
                } ${section.textSize === 'lg' ? 'text-lg' : ''}`}
              >
                <InlineText>{it.body}</InlineText>
              </p>
            )}
          </div>
        ))}
      </div>
      {foot}
      {section.cta && (
        <div className="mt-10">
          <Ctas items={[section.cta]} />
        </div>
      )}
    </>
  )
}
